import { useState, useEffect, useRef } from 'react'
import { useReactToPrint } from 'react-to-print'
import { api } from '../lib/api'
import { Button } from '../components/ui/Button'
import type { Gift } from '../lib/types'
import { YEAR_GROUP_LABELS, RECIPIENT_TYPE_LABELS } from '../lib/types'

interface LabelData {
  orderNumber: number | null
  giftIndex: number
  totalGifts: number
  childName: string
  parentName: string
  gift: Gift
}

interface TicketLabelData {
  customerName: string
  ticketCount: number
}

export default function LabelsPage() {
  const [labels, setLabels] = useState<LabelData[]>([])
  const [ticketLabels, setTicketLabels] = useState<TicketLabelData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const printRef = useRef<HTMLDivElement>(null)
  const ticketPrintRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadLabels()
  }, [])

  const loadLabels = async () => {
    setIsLoading(true)
    setLoadError(false)
    try {
      const orders = await api.getAdminLabels()

      // Generate label data for all gifts (excluding foodbank donations)
      const labelData: LabelData[] = []
      const ticketLabelData: TicketLabelData[] = []

      orders.forEach((order) => {
        const orderNumber = order.orderNumber // Use the database orderNumber
        const allGifts = order.gifts || []

        // Filter out foodbank donations - they don't get labels
        const giftsWithLabels = allGifts.filter(gift => gift.recipientType !== 'foodbank')

        giftsWithLabels.forEach((gift, giftIndex) => {
          labelData.push({
            orderNumber,
            giftIndex: giftIndex + 1,
            totalGifts: giftsWithLabels.length, // Only count non-foodbank gifts
            childName: gift.fromName,
            parentName: order.fullName,
            gift,
          })
        })

        // Generate ticket labels for orders with tokens
        if (order.tokenQuantity > 0) {
          ticketLabelData.push({
            customerName: order.fullName,
            ticketCount: order.tokenQuantity,
          })
        }
      })

      setLabels(labelData)
      setTicketLabels(ticketLabelData)
    } catch (error) {
      console.error('Failed to load labels:', error)
      setLoadError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        body {
          margin: 0;
          padding: 0;
        }
      }
    `,
  })

  const handlePrintTickets = useReactToPrint({
    contentRef: ticketPrintRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        body {
          margin: 0;
          padding: 0;
        }
      }
    `,
  })

  const handleExportCSV = () => {
    // Create CSV header
    const headers = [
      'Order Number',
      'Gift Number',
      'Total Gifts',
      'To',
      'From (Child)',
      'Parent Name',
      'Year Group',
      'Recipient Type',
      'Pickup Location'
    ]

    // Create CSV rows
    const rows = labels.map(label => [
      label.orderNumber ? String(label.orderNumber).padStart(3, '0') : '---',
      label.giftIndex,
      label.totalGifts,
      label.gift.recipientName,
      label.childName,
      label.parentName,
      YEAR_GROUP_LABELS[label.gift.yearGroup],
      RECIPIENT_TYPE_LABELS[label.gift.recipientType],
      label.gift.pickupLocation
    ])

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `gift-labels-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading labels...</p>
      </div>
    )
  }

  if (loadError) {
    return (
      <div role="alert" className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex flex-wrap items-center gap-3">
        <span>Could not load labels. Check your connection and try again.</span>
        <button onClick={() => loadLabels()} className="min-h-[44px] px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Main content - hidden when printing */}
      <div className="print:hidden flex flex-col items-center justify-center py-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Labels</h1>
        <p className="text-gray-600 text-center max-w-md mb-8">
          Generate printable labels for Avery L7160 sheets (63.5mm x 33.9mm, 24 per page).
          Gift labels show order details, token labels show customer name and quantity.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button onClick={handlePrint} size="lg" disabled={labels.length === 0}>
            <svg aria-hidden="true" className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print Gift Labels ({labels.length})
          </Button>
          <Button onClick={handlePrintTickets} size="lg" disabled={ticketLabels.length === 0}>
            <svg aria-hidden="true" className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
            Print Token Labels ({ticketLabels.length})
          </Button>
          <Button onClick={handleExportCSV} variant="outline" size="lg" disabled={labels.length === 0}>
            <svg aria-hidden="true" className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Hidden print containers */}
      <div ref={printRef} className="labels-container">
        {Array.from({ length: Math.ceil(labels.length / 24) }).map((_, pageIndex) => {
          const pageLabels = labels.slice(pageIndex * 24, (pageIndex + 1) * 24)
          return (
            <div key={pageIndex} className="label-page">
              {pageLabels.map((label) => (
                <div
                  key={`${label.orderNumber || 'pending'}-${label.giftIndex}`}
                  className="label-item"
                >
                  <div className="label-content">
                    <div className="label-header">
                      <div className="order-number">
                        {label.orderNumber ? String(label.orderNumber).padStart(3, '0') : '---'}
                      </div>
                      <div className="gift-count">
                        {label.giftIndex} of {label.totalGifts}
                      </div>
                    </div>
                    <div className="to-from">
                      To {label.gift.recipientName}
                      <br />
                      love from {label.childName}
                    </div>
                    <div className="label-footer">
                      <div className="parent-name">
                        {label.parentName}
                      </div>
                      <div className="child-info">
                        {label.childName}
                        <br />
                        <span className="year-group">({YEAR_GROUP_LABELS[label.gift.yearGroup]})</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        })}
      </div>

      <div ref={ticketPrintRef} className="ticket-labels-container">
        {Array.from({ length: Math.ceil(ticketLabels.length / 24) }).map((_, pageIndex) => {
          const pageTickets = ticketLabels.slice(pageIndex * 24, (pageIndex + 1) * 24)
          return (
            <div key={pageIndex} className="ticket-label-page">
              {pageTickets.map((ticket, index) => (
                <div
                  key={`ticket-${pageIndex}-${index}`}
                  className="ticket-label-item"
                >
                  <div className="ticket-label-content">
                    <div className="ticket-customer-name">{ticket.customerName}</div>
                    <div className="ticket-count">
                      {ticket.ticketCount} {ticket.ticketCount === 1 ? 'token' : 'tokens'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        })}
      </div>

      <style>{`
        /* Screen view styles - hide label containers, only visible when printing */
        @media screen {
          .labels-container,
          .ticket-labels-container {
            display: none;
          }
        }

        /* Print styles for A4 with 3×8 label layout */
        @media print {
          .labels-container {
            background: white !important;
          }

          .label-page {
            display: grid;
            grid-template-columns: 7mm 63.5mm 3mm 63.5mm 3mm 63.5mm 7mm;
            grid-template-rows: 12mm repeat(8, 33.9mm) 12mm;
            width: 210mm;
            height: 297mm;
            margin: 0;
            padding: 0;
            background: white !important;
            box-sizing: border-box;
            page-break-after: always;
            page-break-inside: avoid;
          }

          .label-page:last-child {
            page-break-after: auto;
          }

          .label-item {
            width: 63.5mm;
            height: 33.9mm;
            border: none;
            padding: 0;
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
            overflow: hidden;
            background: white !important;
            background-color: white !important;
          }

          /* Position labels in the grid within each page, skipping margin/gap columns */
          .label-item:nth-child(1) { grid-column: 2; grid-row: 2; }
          .label-item:nth-child(2) { grid-column: 4; grid-row: 2; }
          .label-item:nth-child(3) { grid-column: 6; grid-row: 2; }
          .label-item:nth-child(4) { grid-column: 2; grid-row: 3; }
          .label-item:nth-child(5) { grid-column: 4; grid-row: 3; }
          .label-item:nth-child(6) { grid-column: 6; grid-row: 3; }
          .label-item:nth-child(7) { grid-column: 2; grid-row: 4; }
          .label-item:nth-child(8) { grid-column: 4; grid-row: 4; }
          .label-item:nth-child(9) { grid-column: 6; grid-row: 4; }
          .label-item:nth-child(10) { grid-column: 2; grid-row: 5; }
          .label-item:nth-child(11) { grid-column: 4; grid-row: 5; }
          .label-item:nth-child(12) { grid-column: 6; grid-row: 5; }
          .label-item:nth-child(13) { grid-column: 2; grid-row: 6; }
          .label-item:nth-child(14) { grid-column: 4; grid-row: 6; }
          .label-item:nth-child(15) { grid-column: 6; grid-row: 6; }
          .label-item:nth-child(16) { grid-column: 2; grid-row: 7; }
          .label-item:nth-child(17) { grid-column: 4; grid-row: 7; }
          .label-item:nth-child(18) { grid-column: 6; grid-row: 7; }
          .label-item:nth-child(19) { grid-column: 2; grid-row: 8; }
          .label-item:nth-child(20) { grid-column: 4; grid-row: 8; }
          .label-item:nth-child(21) { grid-column: 6; grid-row: 8; }
          .label-item:nth-child(22) { grid-column: 2; grid-row: 9; }
          .label-item:nth-child(23) { grid-column: 4; grid-row: 9; }
          .label-item:nth-child(24) { grid-column: 6; grid-row: 9; }

          .label-content {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 100%;
            height: 100%;
            padding: 3mm;
            background: white !important;
          }

          .label-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 1mm;
          }

          .order-number {
            font-size: 1.5rem;
            font-weight: 700;
            line-height: 1;
            color: #000 !important;
          }

          .gift-count {
            font-size: 0.625rem;
            font-weight: 500;
            color: #000 !important;
            line-height: 1.2;
          }

          .to-from {
            font-size: 0.7rem;
            font-weight: 500;
            color: #000 !important;
            text-align: center;
            line-height: 1.25;
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin: 1mm 0;
          }

          .year-group {
            font-size: 0.6rem;
            color: #000 !important;
            font-style: italic;
          }

          .label-footer {
            display: flex;
            justify-content: space-between;
            margin-top: 1mm;
          }

          .parent-name {
            font-size: 0.55rem;
            color: #000 !important;
            line-height: 1.2;
          }

          .child-info {
            font-size: 0.55rem;
            color: #000 !important;
            text-align: right;
            line-height: 1.2;
          }

          /* Hide everything except labels when printing */
          .print\\:hidden {
            display: none !important;
          }

          /* Ticket labels print styles */
          .ticket-labels-container {
            background: white !important;
          }

          .ticket-label-page {
            display: grid;
            grid-template-columns: 7mm 63.5mm 3mm 63.5mm 3mm 63.5mm 7mm;
            grid-template-rows: 12mm repeat(8, 33.9mm) 12mm;
            width: 210mm;
            height: 297mm;
            margin: 0;
            padding: 0;
            background: white !important;
            box-sizing: border-box;
            page-break-after: always;
            page-break-inside: avoid;
          }

          .ticket-label-page:last-child {
            page-break-after: auto;
          }

          .ticket-label-item {
            width: 63.5mm;
            height: 33.9mm;
            border: none;
            padding: 0;
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
            overflow: hidden;
            background: white !important;
          }

          /* Position ticket labels in the grid */
          .ticket-label-item:nth-child(1) { grid-column: 2; grid-row: 2; }
          .ticket-label-item:nth-child(2) { grid-column: 4; grid-row: 2; }
          .ticket-label-item:nth-child(3) { grid-column: 6; grid-row: 2; }
          .ticket-label-item:nth-child(4) { grid-column: 2; grid-row: 3; }
          .ticket-label-item:nth-child(5) { grid-column: 4; grid-row: 3; }
          .ticket-label-item:nth-child(6) { grid-column: 6; grid-row: 3; }
          .ticket-label-item:nth-child(7) { grid-column: 2; grid-row: 4; }
          .ticket-label-item:nth-child(8) { grid-column: 4; grid-row: 4; }
          .ticket-label-item:nth-child(9) { grid-column: 6; grid-row: 4; }
          .ticket-label-item:nth-child(10) { grid-column: 2; grid-row: 5; }
          .ticket-label-item:nth-child(11) { grid-column: 4; grid-row: 5; }
          .ticket-label-item:nth-child(12) { grid-column: 6; grid-row: 5; }
          .ticket-label-item:nth-child(13) { grid-column: 2; grid-row: 6; }
          .ticket-label-item:nth-child(14) { grid-column: 4; grid-row: 6; }
          .ticket-label-item:nth-child(15) { grid-column: 6; grid-row: 6; }
          .ticket-label-item:nth-child(16) { grid-column: 2; grid-row: 7; }
          .ticket-label-item:nth-child(17) { grid-column: 4; grid-row: 7; }
          .ticket-label-item:nth-child(18) { grid-column: 6; grid-row: 7; }
          .ticket-label-item:nth-child(19) { grid-column: 2; grid-row: 8; }
          .ticket-label-item:nth-child(20) { grid-column: 4; grid-row: 8; }
          .ticket-label-item:nth-child(21) { grid-column: 6; grid-row: 8; }
          .ticket-label-item:nth-child(22) { grid-column: 2; grid-row: 9; }
          .ticket-label-item:nth-child(23) { grid-column: 4; grid-row: 9; }
          .ticket-label-item:nth-child(24) { grid-column: 6; grid-row: 9; }

          .ticket-label-content {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
            padding: 3mm;
            background: white !important;
            text-align: center;
          }

          .ticket-customer-name {
            font-size: 0.85rem;
            font-weight: 600;
            color: #000 !important;
            line-height: 1.3;
            margin-bottom: 2mm;
          }

          .ticket-count {
            font-size: 0.75rem;
            font-weight: 500;
            color: #000 !important;
            line-height: 1.2;
          }
        }
      `}</style>
    </div>
  )
}
